/**
 * This component is rendered only once during the application lifecycle.
 * It is the root component of the application.
 * You can add any global context providers here.
 */
import { captureError } from "@features/activities/data";
import { __DEV__ } from "@features/shared/presentation";
import { refresh } from "@taager/react-router";
import { Component, type PropsWithChildren, type ReactNode } from "react";

type ErrorState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: object | null;
};

// ErrorBoundary class component to catch errors in the child components
class ErrorBoundary extends Component<PropsWithChildren> {
  public state: ErrorState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  // Update state when an error is caught
  public static getDerivedStateFromError(error): { hasError: true } {
    // eslint-disable-next-line no-console
    console.log("ErrorBoundary caught an error:", error);

    return { hasError: true };
  }

  // Option to reset the error state and recover the app
  public handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  // Log the error details
  public componentDidCatch(error, errorInfo): void {
    console.error("Error occurred:", error, errorInfo);
    this.setState({ error, errorInfo });
    // You can log to an external service like Sentry here
  }

  public render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;

    // Render a fallback UI when an error is caught
    if (hasError && error) {
      captureError(error);

      if (!__DEV__) {
        return (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <h2>Something went wrong.</h2>
            <p>
              An unexpected error has occurred. Please try refreshing the page
              or click the button below to retry.
            </p>
            <p>{error?.message}</p>
            <button onClick={refresh} style={{ padding: "10px 20px" }}>
              Try Again
            </button>
          </div>
        );
      }

      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h2>Something went wrong.</h2>
          <p>
            An unexpected error has occurred. Please try refreshing the page or
            click the button below to retry.
            {error?.message}
          </p>
          <button onClick={this.handleReset} style={{ padding: "10px 20px" }}>
            Try Again
          </button>
          <details style={{ whiteSpace: "pre-wrap", marginTop: "20px" }}>
            {error && error.toString()}
            <br />
            {errorInfo && (errorInfo as any).componentStack}
          </details>
        </div>
      );
    }

    // If no error, render children components as usual
    return this.props.children;
  }
}

export default function App({ children }: PropsWithChildren) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}
